"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight, Search, X } from "lucide-react";
import { useAuth } from "@/lib/auth/context";
import { useTranslation } from "@/lib/i18n/context";
import { availableHostingFeatures } from "@/lib/hosting/features";
import { useTheme } from "@/lib/theme/context";
import { THEMES, type ThemeId } from "@/lib/theme/themes";

type Command = {
  id: string;
  title: string;
  description: string;
  category: string;
  href?: string;
  action?: () => void;
};

export function CustomerCommandPalette({
  isOpen,
  onClose,
  onOpen,
}: {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}) {
  const { permissions, capabilities, activeAccountId, user } = useAuth();
  const { t } = useTranslation();
  const { setTheme } = useTheme();
  const router = useRouter();
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement | null>(null);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const [sitesAccount, setSitesAccount] = useState<string | null>(null);
  const [sites, setSites] = useState<{ id: string; domain: string; name: string }[]>([]);

  const canReadSites = permissions.includes("site.read") && capabilities?.webHostingEnabled === true;

  const commands: Command[] = [
    {
      id: "home",
      title: t("portal.tools.title"),
      description: t("portal.tools.subtitle"),
      category: t("portal.tools.account"),
      href: "/portal",
    },
    ...availableHostingFeatures(permissions, capabilities).map((feature) => ({
      id: feature.id,
      title: t(feature.titleKey),
      description: t(feature.descriptionKey),
      category: t(`portal.tools.groups.${feature.group}`),
      href: feature.href,
    })),
    ...(permissions.includes("site.create") && capabilities?.webHostingEnabled
      ? [
          {
            id: "create-site",
            title: t("portal.search.createSite"),
            description: t("portal.search.createSiteDescription"),
            category: t("portal.tools.groups.websites"),
            href: "/portal/sites/new",
          },
        ]
      : []),
    ...(canReadSites && sitesAccount === activeAccountId
      ? sites.map((site) => ({
          id: `site-${site.id}`,
          title: site.domain || site.name,
          description: site.name,
          category: t("portal.tools.groups.websites"),
          href: `/portal/sites/${site.id}`,
        }))
      : []),
    ...THEMES.map((theme) => ({
      id: `theme-${theme.id}`,
      title: t("portal.search.theme", { name: theme.name }),
      description: t("portal.search.changeAppearance"),
      category: t("portal.search.appearance"),
      action: () => setTheme(theme.id as ThemeId),
    })),
  ];

  const searchTrimmed = query.trim().toLocaleLowerCase();
  const filtered = commands.filter((command) =>
    `${command.title} ${command.description} ${command.category}`.toLocaleLowerCase().includes(searchTrimmed)
  );

  const choose = (command?: Command) => {
    if (!command) return;
    onClose();
    if (command.href) router.push(command.href);
    else command.action?.();
  };

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelected(0);
      dialog.current?.showModal();
      input.current?.focus();
    } else {
      dialog.current?.close();
    }
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (!user) return;
    const shortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        event.stopPropagation();
        if (isOpen) {
          onClose();
        } else {
          onOpen();
        }
      }
    };
    document.addEventListener("keydown", shortcut, true);
    return () => document.removeEventListener("keydown", shortcut, true);
  }, [isOpen, onClose, onOpen, user]);

  useEffect(() => {
    setSites([]);
    setSitesAccount(null);
    if (!isOpen || !canReadSites || !activeAccountId) return;
    const controller = new AbortController();
    fetch("/api/portal/sites", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) return;
        const payload = await response.json();
        if (!controller.signal.aborted) {
          setSites(Array.isArray(payload.sites) ? payload.sites : []);
          setSitesAccount(activeAccountId);
        }
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [isOpen, canReadSites, activeAccountId]);

  const index = Math.min(selected, Math.max(0, filtered.length - 1));

  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [index]);

  if (!user) return null;

  const activeCommand = filtered[index];

  return (
    <dialog
      ref={dialog}
      onCancel={onClose}
      aria-label={t("portal.search.title")}
      className="m-auto w-[calc(100%-2rem)] max-w-xl max-h-[85vh] rounded-2xl border border-themed-border bg-surface-card p-0 text-themed-textPrimary shadow-2xl backdrop:bg-black/60 overflow-hidden open:flex flex-col"
      onKeyDown={(event) => {
        if (event.target !== input.current) return;
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelected((value) => Math.max(0, Math.min(value + 1, filtered.length - 1)));
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelected((value) => Math.max(0, value - 1));
        } else if (event.key === "Enter") {
          event.preventDefault();
          choose(filtered[index]);
        }
      }}
    >
      <div className="flex items-center gap-3 border-b border-themed-border p-4">
        <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-themed-textSecondary" />
        <input
          ref={input}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setSelected(0);
          }}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls="customer-command-results"
          aria-activedescendant={activeCommand ? `command-option-${activeCommand.id}` : undefined}
          aria-label={t("portal.tools.search")}
          placeholder={t("portal.tools.search")}
          className="min-w-0 flex-1 bg-transparent text-sm text-themed-textPrimary placeholder:text-themed-textSecondary outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:rounded"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.actions.close")}
          className="rounded-lg p-1.5 text-themed-textSecondary hover:bg-surface-muted hover:text-themed-textPrimary transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent-primary)] shrink-0"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {query.trim()
          ? t("portal.search.resultsCount", { count: filtered.length })
          : ""}
      </div>

      <div
        id="customer-command-results"
        role="listbox"
        aria-label={t("portal.search.title")}
        className="max-h-[60vh] space-y-1 overflow-y-auto p-2"
      >
        {!filtered.length && (
          <p className="p-5 text-center text-sm text-themed-textSecondary">
            {t("portal.search.noResults")}
          </p>
        )}
        {filtered.map((command, position) => {
          const isSelected = position === index;
          return (
            <button
              key={command.id}
              ref={isSelected ? selectedItemRef : undefined}
              id={`command-option-${command.id}`}
              role="option"
              aria-selected={isSelected}
              onMouseEnter={() => setSelected(position)}
              onFocus={() => setSelected(position)}
              onClick={() => choose(command)}
              className={`flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent-primary)] ${
                isSelected ? "bg-surface-muted" : "hover:bg-surface-muted"
              }`}
            >
              <div className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-themed-textPrimary">
                  {command.title}
                </span>
                <span className="block truncate text-xs text-themed-textSecondary">
                  {command.description}
                </span>
              </div>
              <span className="hidden sm:inline-block shrink-0 rounded bg-surface-card px-2 py-0.5 text-xs text-themed-textSecondary border border-themed-border">
                {command.category}
              </span>
              <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0 text-themed-textMuted" />
            </button>
          );
        })}
      </div>

      <div className="border-t border-themed-border px-4 py-2.5 text-xs text-themed-textSecondary flex items-center justify-between">
        <span>{t("portal.search.keyboard")}</span>
        <span>
          {t("portal.search.resultsCount", { count: filtered.length })}
        </span>
      </div>
    </dialog>
  );
}
